<script setup lang="ts">
import { object, string } from "yup";
import type ApiError from "~/plugins/errors/apiError";
import type { BasicUserRole } from "~/models/User";
import isObject from "lodash/isObject";
import isEmpty from "lodash/isEmpty";
import { useDebounce } from "@vueuse/core";

const props = defineProps<{
    modelValue: boolean;
}>();

const emit = defineEmits<{
    (e: "update:modelValue", v: boolean): void;
    (e: "submit", v: SubmitEvent): void;
}>();

const { user } = useUser();
const form = ref();
const submitting = ref<boolean>(false);
const success = ref<boolean>(false);
const email = ref<string>("");
const errors = ref<Record<string, string[]>>();
const schema = object({
    email: string().email().required("Please insert an email"),
});
const state = ref<{
    role: BasicUserRole;
    email: string;
    first_name?: string;
}>({
    role: "user",
    email: "",
});

watch(
    () => props.modelValue,
    () => {
        state.value.email = "";
        success.value = false;
        errors.value = undefined;
    },
);

async function submit(e: SubmitEvent) {
    try {
        submitting.value = true;
        success.value = false;

        await form.value!.validate();

        // Do something with state.value
        const { message } = await useApi().users.invite(state.value);

        success.value = true;
        submitting.value = false;

        if (message === "CONNECTED_TO_ACCOUNT") {
            useToast().add({
                title: `Connected successfully`,
                description: `${state.value.email} has been connected to ${user.value.account.name}!`,
            });
        } else {
            useToast().add({
                title: `Invite Successful`,
                description: `${state.value.email} has been invited to HireData!`,
            });
        }

        email.value = state.value.email;
        state.value.email = "";
        state.value.role = "user";
        refreshNuxtData();

        emit("submit", e);
        emit("update:modelValue", false);
    } catch (e: ApiError | any) {
        errors.value = e?.errors || [];
        submitting.value = false;
    }
}

const { t } = useI18n();

const roles = computed(() => {
    const roles = ["user"];

    if (user.value?.is_super_admin || user.value?.roles?.includes("owner")) {
        roles.push("admin", "owner");
    } else if (user.value?.roles?.includes("admin")) {
        roles.push("admin");
    }

    return roles.map((role) => ({
        label: t(`labels.${role}`),
        value: role,
    }));
});

const debouncableEmail = computed(() => state.value.email);
const { data: emailOptionsData } = useAsyncData("email-options", async () => {
    if (!user.value.is_super_admin) {
        return [];
    }

    if (isObject(state.value.email)) {
        const email = state.value.email as { label: string, value: string };
        state.value.email = email?.value ?? email?.label;
    }

    if (!isEmpty(state.value.email)) {
        const { data } = await useApi().getResourceList("all_users", {
            where_has_user: false,
        });

        return data?.map((option) => ({
            ...option,
            label: `${option.label} (${option.metadata.email})`,
            value: option.metadata.email,
        }));
    }

    return [];
}, {
    watch: [useDebounce(debouncableEmail, 450)],
});

const emailOptions = computed(() => emailOptionsData.value ?? []);
</script>

<template>
    <UModal
        :model-value="modelValue"
        @update:model-value="emit('update:modelValue', $event)"
    >
        <UForm
            ref="form"
            :schema="schema"
            :state="state"
            @submit="submit"
            :validate-on="['submit']"
        >
            <UCard>
                <template #header>
                    <h2 class="font-display font-medium text-lg">Add a user</h2>
                </template>
                <template #default>
                    <div class="flex flex-col gap-4">
                        <UFormGroup
                            size="lg"
                            label="Email"
                            name="email"
                            type="email"
                            required
                            :error="
                                errors && errors.email
                                    ? errors.email.join(', ')
                                    : undefined
                            "
                        >
                            <USelectMenu
                                v-if="user?.is_super_admin"
                                v-model="state.email"
                                v-model:query="state.email"
                                :options="emailOptions"
                                searchable
                                creatable
                                placeholder="Search for an email..."
                                searchable-placeholder="Search for an email..."
                                size="lg"
                                show-create-option-when="always"
                            >
                                <template #empty>
                                    Type an email to search for a user
                                </template>
                            </USelectMenu>
                            <UInput
                                v-else
                                v-model="state.email"
                                type="email"
                                autofocus
                                placeholder="Enter an email..."
                                size="lg"
                                required
                            />
                        </UFormGroup>
                        <UFormGroup
                            v-if="roles.length"
                            size="lg"
                            label="Role"
                            name="role"
                            type="role"
                            required
                        >
                            <UISelectMenu
                                size="lg"
                                :options="roles"
                                value-attribute="value"
                                v-model="state.role"
                            />
                        </UFormGroup>
                    </div>
                </template>
                <template #footer>
                    <UButton
                        type="submit"
                        size="lg"
                        block
                        :disabled="submitting"
                        :loading="submitting"
                    >
                        {{
                            state.first_name
                                ? `Invite ${state.first_name}`
                                : `Invite`
                        }}
                    </UButton>
                </template>
            </UCard>
        </UForm>
    </UModal>
</template>

<style scoped></style>
